import { useState, useEffect, useCallback } from 'react';
import db from '@/db';
import { useAuth } from '@/context/AuthContext';
import { refreshOperatorsMirror } from '@/lib/operatorsMirror';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Shield, Coffee, DollarSign, Bike, ChefHat, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin', icon: Shield, color: 'bg-blue-600' },
  { value: 'garcom', label: 'Garçom', icon: Coffee, color: 'bg-emerald-600' },
  { value: 'caixa', label: 'Caixa', icon: DollarSign, color: 'bg-amber-600' },
  { value: 'cozinha', label: 'Cozinha', icon: ChefHat, color: 'bg-orange-600' },
  { value: 'entregador', label: 'Entregador', icon: Bike, color: 'bg-purple-600' },
];

const emptyForm = { name: '', pin: '', role: 'garcom', active: true, whatsapp: '' };

async function apiCall(path, method, body) {
  const resp = await fetch(`${API_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const detail = typeof data?.detail === 'string' ? data.detail : `Falha (HTTP ${resp.status})`;
    throw new Error(detail);
  }
  return data;
}

export default function EquipePage() {
  const { operator: current, getSessionPin } = useAuth();
  const [operators, setOperators] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  // v7.89 — Trava do Admin Master: PIN mantido em memória durante a visita
  const [masterPin, setMasterPin] = useState(() => getSessionPin() || '');
  const [gatePin, setGatePin] = useState('');
  const [gateChecking, setGateChecking] = useState(false);

  const load = useCallback(async () => {
    await refreshOperatorsMirror();
    const all = await db.operators.toArray();
    setOperators(all.sort((a, b) => (a.role || '').localeCompare(b.role || '') || (a.name || '').localeCompare(b.name || '')));
  }, []);

  useEffect(() => { load(); }, [load]);

  const isMaster = !!current?.isMaster;

  const unlockGate = async () => {
    const pin = gatePin.trim();
    if (pin.length < 4) return toast.error('PIN inválido');
    setGateChecking(true);
    try {
      const data = await apiCall('/api/operators/verify', 'POST', { pin });
      if (!data?.operator?.isMaster) {
        toast.error('Este PIN não é do Admin Master');
      } else {
        setMasterPin(pin);
        toast.success('Acesso liberado');
      }
    } catch (e) {
      toast.error(e.message || 'PIN incorreto');
    }
    setGateChecking(false);
    setGatePin('');
  };

  const openCreate = () => { setEditing({ isNew: true }); setForm(emptyForm); };
  const openEdit = (op) => {
    setEditing(op);
    setForm({ name: op.name, pin: '', role: op.role || 'garcom', active: op.active !== false, whatsapp: op.whatsapp || '' });
  };
  const closeDialog = () => { setEditing(null); setForm(emptyForm); };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Nome é obrigatório');
    if (editing?.isNew && (form.pin.length < 4 || form.pin.length > 6)) {
      return toast.error('PIN precisa de 4 a 6 dígitos');
    }
    if (!editing?.isNew && form.pin && (form.pin.length < 4 || form.pin.length > 6)) {
      return toast.error('Novo PIN precisa de 4 a 6 dígitos');
    }
    const whatsappDigits = form.whatsapp.replace(/\D/g, '');
    if (whatsappDigits && (whatsappDigits.length < 10 || whatsappDigits.length > 13)) {
      return toast.error('WhatsApp inválido. Use DDD + número (ex: 21999998888)');
    }
    setSaving(true);
    try {
      if (editing?.isNew) {
        await apiCall('/api/operators', 'POST', {
          masterPin,
          name: form.name.trim(),
          pin: form.pin,
          role: form.role,
          active: form.active,
          whatsapp: whatsappDigits,
        });
        toast.success(`"${form.name.trim()}" cadastrado no servidor!`);
      } else {
        await apiCall(`/api/operators/${editing.opId}`, 'PUT', {
          masterPin,
          name: form.name.trim(),
          role: form.role,
          active: form.active,
          whatsapp: whatsappDigits,
          ...(form.pin ? { pin: form.pin } : {}),
        });
        toast.success(`"${form.name.trim()}" atualizado!${form.pin ? ' Novo PIN já vale em todos os aparelhos.' : ''}`);
      }
      closeDialog();
      load();
    } catch (e) {
      toast.error(e.message || 'Falha ao salvar');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setSaving(true);
    try {
      await apiCall(`/api/operators/${confirmDelete.opId}/delete`, 'POST', { masterPin });
      toast.success(`"${confirmDelete.name}" excluído DEFINITIVAMENTE do servidor`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e.message || 'Falha ao excluir');
    }
    setSaving(false);
  };

  const toggleActive = async (op) => {
    try {
      await apiCall(`/api/operators/${op.opId}`, 'PUT', { masterPin, active: op.active === false });
      load();
    } catch (e) {
      toast.error(e.message || 'Falha ao alterar status');
    }
  };

  // ── Guardas de acesso ──────────────────────────────────────────────
  if (!isMaster) {
    return (
      <div data-testid="equipe-page" className="p-6 lg:p-8">
        <div data-testid="equipe-blocked" className="max-w-md mx-auto mt-16 bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-4">
            <Lock size={20} strokeWidth={1.75} />
          </div>
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Acesso restrito</h2>
          <p className="text-sm text-slate-500">
            Apenas o <strong>Admin Master</strong> pode cadastrar, alterar ou excluir operadores.
          </p>
        </div>
      </div>
    );
  }

  if (!masterPin) {
    return (
      <div data-testid="equipe-page" className="p-6 lg:p-8">
        <div data-testid="equipe-gate" className="max-w-md mx-auto mt-16 bg-white border border-slate-200 rounded-2xl p-8">
          <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-4">
            <Lock size={20} strokeWidth={1.75} />
          </div>
          <h2 className="font-heading text-xl font-bold text-slate-900 text-center mb-2">Confirme o PIN do Admin Master</h2>
          <p className="text-sm text-slate-500 text-center mb-6">Toda alteração na equipe é autorizada e gravada no servidor.</p>
          <Input
            data-testid="equipe-gate-pin"
            type="password"
            inputMode="numeric"
            value={gatePin}
            onChange={(e) => setGatePin(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => { if (e.key === 'Enter') unlockGate(); }}
            placeholder="••••"
            maxLength={8}
            className="h-12 border border-slate-200 rounded-lg font-mono tracking-widest text-lg text-center mb-4"
          />
          <button
            data-testid="equipe-gate-submit"
            onClick={unlockGate}
            disabled={gateChecking}
            className="w-full h-12 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-50"
          >
            {gateChecking ? 'Verificando…' : 'Liberar acesso'}
          </button>
        </div>
      </div>
    );
  }

  const grouped = operators.reduce((acc, o) => {
    const r = o.role || 'outro';
    (acc[r] = acc[r] || []).push(o);
    return acc;
  }, {});

  return (
    <div data-testid="equipe-page" className="p-6 lg:p-8">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-3xl tracking-tight font-bold text-slate-900">Equipe</h2>
          <p className="text-sm text-slate-500 mt-1">
            Operadores gravados no servidor com PIN criptografado (bcrypt) — exclusão é definitiva
          </p>
        </div>
        <button
          data-testid="new-operator-btn"
          onClick={openCreate}
          className="flex items-center gap-2 bg-blue-600 text-white rounded-xl font-bold px-5 py-3 min-h-[44px] active:translate-y-[1px] transition-all hover:bg-blue-700"
        >
          <Plus size={18} strokeWidth={1.75} /> Novo Operador
        </button>
      </div>

      {ROLE_OPTIONS.map((r) => {
        const Icon = r.icon;
        const list = grouped[r.value] || [];
        return (
          <div key={r.value} className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-7 h-7 rounded-lg ${r.color} text-white flex items-center justify-center`}>
                <Icon size={14} strokeWidth={1.75} />
              </div>
              <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">{r.label}</p>
              <span className="text-[10px] font-mono text-slate-400">({list.length})</span>
            </div>
            {list.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-5 text-center text-sm text-slate-400">
                Nenhum {r.label.toLowerCase()} cadastrado
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {list.map((op) => (
                  <div key={op.opId} data-testid={`op-item-${op.opId}`} className={`bg-white border rounded-xl p-4 transition-all ${op.active !== false ? 'border-slate-200' : 'border-slate-100 opacity-60'}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600">
                          {op.name?.[0]?.toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-slate-900 truncate">{op.name}</p>
                          <p className="text-[10px] font-mono text-slate-400">PIN: {'•'.repeat(op.pinLength || 4)}{op.whatsapp ? ` · 📱 ${op.whatsapp}` : ''}</p>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-wrap justify-end">
                        {op.isMaster && <Badge data-testid={`op-master-${op.opId}`} className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">Master</Badge>}
                        {op.active === false && <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[10px]">Inativo</Badge>}
                        {op.opId === current?.opId && <Badge data-testid={`op-self-${op.opId}`} className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">Você</Badge>}
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                        <Switch data-testid={`op-toggle-${op.opId}`} checked={op.active !== false} disabled={op.isMaster} onCheckedChange={() => toggleActive(op)} />
                        Ativo
                      </label>
                      <div className="flex gap-1">
                        <button data-testid={`op-edit-${op.opId}`} onClick={() => openEdit(op)} className="w-9 h-9 rounded-lg border border-slate-200 hover:border-slate-900 flex items-center justify-center transition-all"><Pencil size={13} strokeWidth={1.75} /></button>
                        {!op.isMaster && (
                          <button data-testid={`op-delete-${op.opId}`} onClick={() => setConfirmDelete(op)} className="w-9 h-9 rounded-lg border border-slate-200 hover:border-red-500 hover:text-red-600 flex items-center justify-center transition-all"><Trash2 size={13} strokeWidth={1.75} /></button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Edit/Create Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent data-testid="op-dialog" className="max-w-md bg-white border border-slate-200 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">{editing?.isNew ? 'Novo Operador' : 'Editar Operador'}</DialogTitle>
            <DialogDescription>
              Gravado direto no servidor com PIN criptografado. {editing?.isNew ? '' : 'Deixe o PIN em branco para manter o atual.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-1 block">Nome</label>
              <Input data-testid="op-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Maria" className="h-11 border border-slate-200 rounded-lg" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-1 block">
                {editing?.isNew ? 'PIN (4 a 6 dígitos)' : 'Novo PIN (opcional)'}
              </label>
              <Input data-testid="op-pin-input" type="text" inputMode="numeric" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '') })} placeholder={editing?.isNew ? '1234' : 'Manter atual'} className="h-11 border border-slate-200 rounded-lg font-mono tracking-widest text-lg" maxLength={6} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-1 block">WhatsApp (opcional)</label>
              <Input data-testid="op-whatsapp-input" type="tel" inputMode="tel" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value.replace(/[^\d\s()+-]/g, '') })} placeholder="Ex: 21999998888" className="h-11 border border-slate-200 rounded-lg font-mono" maxLength={20} />
              <p className="text-[11px] text-slate-400 mt-1">Recebe alertas de pedidos: Cozinha/Caixa (novo pedido), Entregador (pronto), Admin (demora)</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-1 block">Função</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={!!editing?.isMaster}>
                <SelectTrigger data-testid="op-role-select" className="h-11 border border-slate-200 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-3 pt-2 border-t border-slate-100">
              <Switch data-testid="op-active-switch" checked={form.active} disabled={!!editing?.isMaster} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <div>
                <p className="text-sm font-bold text-slate-900">Ativo</p>
                <p className="text-xs text-slate-500">Operadores inativos não podem fazer login</p>
              </div>
            </label>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <button onClick={closeDialog} className="px-5 h-10 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancelar</button>
            <button data-testid="save-op-btn" onClick={handleSave} disabled={saving} className="px-5 h-10 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50">{saving ? 'Salvando…' : 'Salvar'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}>
        <DialogContent className="max-w-sm bg-white border border-slate-200 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">Excluir "{confirmDelete?.name}" definitivamente?</DialogTitle>
            <DialogDescription>Exclusão física no servidor (hard delete). Não há lixeira — o operador NÃO volta.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-lg border border-slate-200 font-bold text-slate-600 hover:bg-slate-50">Cancelar</button>
            <button data-testid="op-confirm-delete-btn" onClick={handleDelete} disabled={saving} className="px-4 py-2 rounded-lg bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-50">{saving ? 'Excluindo…' : 'Excluir definitivamente'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
